'use client';

import { useEffect, useState } from 'react';

const ANALYSIS_STEPS = [
  { label: 'Parsing schedule structure', detail: 'Reading activities, calendars, and WBS' },
  { label: 'Mapping relationships', detail: 'Mapping relationships' },
  { label: 'Computing float values', detail: 'Calculating early/late dates and total float' },
  { label: 'Evaluating DCMA criteria', detail: 'Running 12-point quality assessment' },
  { label: 'Generating score', detail: 'Applying thresholds and computing grade' },
];

interface AnalyzingAnimationProps {
  onComplete: () => void;
}

export function AnalyzingAnimation({ onComplete }: AnalyzingAnimationProps) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    // Each step takes ~800ms, total ~4s
    const interval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev >= ANALYSIS_STEPS.length - 1) {
          clearInterval(interval);
          // Small delay after last step before revealing results
          setTimeout(onComplete, 600);
          return prev;
        }
        return prev + 1;
      });
    }, 800);

    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <div className="flex flex-col items-center gap-10 py-24 sm:py-32">
      {/* Animated ring */}
      <div className="relative flex items-center justify-center">
        <div className="h-20 w-20 rounded-full border-[3px] border-muted" />
        <div className="absolute h-20 w-20 rounded-full border-[3px] border-transparent border-t-primary animate-spin" />
        <div className="absolute text-sm font-bold text-primary font-mono">
          {Math.min(Math.round(((currentStep + 1) / ANALYSIS_STEPS.length) * 100), 100)}%
        </div>
      </div>

      {/* Steps */}
      <div className="w-full max-w-sm space-y-3">
        {ANALYSIS_STEPS.map((step, idx) => {
          const isActive = idx === currentStep;
          const isDone = idx < currentStep;

          return (
            <div
              key={step.label}
              className={`flex items-center gap-3 transition-all duration-300 ${
                isActive ? 'opacity-100' : isDone ? 'opacity-40' : 'opacity-20'
              }`}
            >
              {/* Step indicator */}
              <div className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                {isDone ? (
                  <svg
                    className="h-5 w-5 text-primary"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2.5}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                ) : isActive ? (
                  <div className="h-2.5 w-2.5 rounded-full bg-primary animate-pulse" />
                ) : (
                  <div className="h-2 w-2 rounded-full bg-muted-foreground/30" />
                )}
              </div>

              {/* Step text */}
              <div>
                <p className={`text-sm font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>
                  {step.label}
                </p>
                {isActive && (
                  <p className="text-xs text-muted-foreground/70 animate-in fade-in duration-300">{step.detail}</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
